import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FolderOpen,
  Upload,
  Search,
  Copy,
  Trash2,
  ExternalLink,
  Eye,
  RefreshCw,
  Image as ImageIcon,
  Check,
  HardDrive,
  Layers,
} from 'lucide-react';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { storage } from '../../lib/supabase/storage';
import { isSupabaseConfigured } from '../../lib/supabase/client';

export default function AdminMediaLibrary() {
  const [selectedBucket, setSelectedBucket] = useState('website-assets');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fileInputRef = useRef(null);
  const toast = useToast();

  const buckets = [
    { id: 'website-assets', label: 'Website Assets (Hero, Logos, Content)' },
    { id: 'project-images', label: 'Project Portfolio Images' },
    { id: 'gallery', label: 'Showcase Gallery Images' },
    { id: 'testimonial-images', label: 'Client Testimonial Avatars' },
  ];

  const loadFiles = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const list = await storage.listFiles(selectedBucket);
      setFiles(list);
    } catch (err) {
      console.error('Error loading bucket files:', err);
      toast.error(`Failed to load files from ${selectedBucket}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedBucket, toast]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleFileUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files || []);
    if (uploadedFiles.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (const file of uploadedFiles) {
      try {
        await storage.uploadFile(selectedBucket, file);
        successCount++;
      } catch (err) {
        console.error(`Upload error for ${file.name}:`, err);
        toast.error(`Failed to upload ${file.name}: ${err.message}`);
      }
    }

    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file(s) to ${selectedBucket}`);
      loadFiles();
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setUploading(false);
  };

  const handleCopyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success('Public CDN URL copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async () => {
    if (!fileToDelete) return;
    try {
      await storage.deleteFile(fileToDelete.bucket, fileToDelete.storagePath);
      toast.success('File deleted from Supabase Storage');
      setDeleteConfirmOpen(false);
      setFileToDelete(null);
      loadFiles();
    } catch (err) {
      console.error('Error deleting file:', err);
      toast.error('Failed to delete file: ' + err.message);
    }
  };

  const filteredFiles = files.filter((f) =>
    (f.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="space-y-8">
      {/* Top Banner Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-slate-900 to-slate-950 p-6 sm:p-8 rounded-3xl border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-[#9BB7A3]/10 text-[#9BB7A3] border border-[#9BB7A3]/20">
              <HardDrive size={13} />
              Supabase Storage CDN
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
            Media & Asset Library
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage, upload, preview, and copy public URLs for studio images and media assets.
          </p>
        </div>

        <div className="flex items-center gap-3 relative z-10">
          <button
            onClick={loadFiles}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 transition"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 text-xs font-bold transition shadow-lg shadow-[#9BB7A3]/20"
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Upload size={14} />
                <span>Upload Assets</span>
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/svg+xml,image/gif,application/pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* Bucket Selector Tabs */}
      <div className="flex flex-wrap items-center gap-2 bg-slate-900/60 p-2 rounded-2xl border border-slate-800">
        {buckets.map((b) => {
          const isActive = selectedBucket === b.id;
          return (
            <button
              key={b.id}
              onClick={() => setSelectedBucket(b.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                isActive
                  ? 'bg-[#9BB7A3] text-slate-950 shadow-md font-bold'
                  : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800'
              }`}
            >
              <FolderOpen size={16} />
              <span>{b.label}</span>
            </button>
          );
        })}
      </div>

      {/* Search & Stats Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search assets by filename..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-[#9BB7A3] focus:ring-1 focus:ring-[#9BB7A3]"
          />
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Layers size={14} className="text-[#9BB7A3]" />
          <span>
            {filteredFiles.length} {filteredFiles.length === 1 ? 'file' : 'files'} in{' '}
            <strong className="text-slate-200">{selectedBucket}</strong>
          </span>
        </div>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="py-24 flex flex-col items-center justify-center gap-3">
          <div className="w-10 h-10 border-3 border-[#9BB7A3]/30 border-t-[#9BB7A3] rounded-full animate-spin"></div>
          <p className="text-xs text-slate-400">Loading assets from Supabase Storage...</p>
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-12 text-center max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-slate-800/80 text-slate-400 flex items-center justify-center mx-auto mb-4 border border-slate-700">
            <ImageIcon size={32} />
          </div>
          <h3 className="text-base font-bold text-white mb-1">No Assets Found</h3>
          <p className="text-xs text-slate-400 leading-relaxed mb-6">
            {searchQuery
              ? `No files matching "${searchQuery}" in this bucket.`
              : `The storage bucket "${selectedBucket}" is currently empty. Click below to upload assets.`}
          </p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs rounded-xl transition shadow-lg shadow-[#9BB7A3]/20"
          >
            <Upload size={15} />
            <span>Upload Image Now</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filteredFiles.map((file) => {
            const isImage = file.name?.match(/\.(jpg|jpeg|png|webp|gif|svg)$/i);
            const isCopied = copiedId === file.name;

            return (
              <div
                key={file.name}
                className="group bg-slate-900 border border-slate-800 hover:border-[#9BB7A3]/50 rounded-2xl overflow-hidden shadow-lg transition-all duration-300 flex flex-col relative"
              >
                {/* Thumbnail */}
                <div
                  className="aspect-square bg-slate-950 flex items-center justify-center relative overflow-hidden cursor-pointer"
                  onClick={() => setPreviewFile(file)}
                >
                  {isImage ? (
                    <img
                      src={file.publicUrl}
                      alt={file.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-slate-500 p-4 text-center">
                      <ImageIcon size={32} className="mb-1 text-slate-600" />
                      <span className="text-[10px] uppercase font-bold tracking-wider">Asset</span>
                    </div>
                  )}

                  {/* Hover Overlay Actions */}
                  <div className="absolute inset-0 bg-slate-950/80 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2 p-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewFile(file);
                      }}
                      className="p-2 rounded-xl bg-slate-800 hover:bg-[#9BB7A3] text-slate-200 hover:text-slate-950 transition shadow-md"
                      title="Preview Image"
                    >
                      <Eye size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyUrl(file.publicUrl, file.name);
                      }}
                      className="p-2 rounded-xl bg-slate-800 hover:bg-[#9BB7A3] text-slate-200 hover:text-slate-950 transition shadow-md"
                      title="Copy Public URL"
                    >
                      {isCopied ? <Check size={15} className="text-emerald-400" /> : <Copy size={15} />}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFileToDelete(file);
                        setDeleteConfirmOpen(true);
                      }}
                      className="p-2 rounded-xl bg-rose-950/80 hover:bg-rose-600 text-rose-300 hover:text-white transition shadow-md"
                      title="Delete from Storage"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* File Meta Info */}
                <div className="p-3 bg-slate-900 border-t border-slate-800/80 flex flex-col justify-between flex-1">
                  <div className="text-xs font-semibold text-slate-200 truncate" title={file.name}>
                    {file.name}
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-400 mt-2">
                    <span>{file.metadata?.size ? formatFileSize(file.metadata.size) : 'File'}</span>
                    <button
                      onClick={() => handleCopyUrl(file.publicUrl, file.name)}
                      className="text-[#9BB7A3] hover:underline font-bold"
                    >
                      {isCopied ? 'Copied!' : 'Copy URL'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Image Preview Modal */}
      {previewFile && (
        <Modal
          isOpen={Boolean(previewFile)}
          onClose={() => setPreviewFile(null)}
          title="Asset Details & Preview"
          maxWidth="max-w-3xl"
        >
          <div className="space-y-6">
            <div className="rounded-2xl overflow-hidden bg-slate-950 border border-slate-800 max-h-[480px] flex items-center justify-center p-2">
              <img
                src={previewFile.publicUrl}
                alt={previewFile.name}
                className="max-h-[440px] w-auto max-w-full object-contain rounded-xl"
              />
            </div>

            <div className="space-y-3 bg-slate-950/60 p-4 rounded-2xl border border-slate-800">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">File Name:</span>
                <span className="text-xs font-mono font-bold text-slate-200">{previewFile.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Storage Bucket:</span>
                <span className="text-xs font-bold text-[#9BB7A3]">{previewFile.bucket}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 block mb-1">Public CDN URL:</span>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={previewFile.publicUrl}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs font-mono text-slate-300 select-all"
                  />
                  <button
                    onClick={() => handleCopyUrl(previewFile.publicUrl, 'modal')}
                    className="px-4 py-2 bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 text-xs font-bold rounded-xl transition shrink-0"
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <a
                href={previewFile.publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white"
              >
                <span>Open original in new tab</span>
                <ExternalLink size={13} />
              </a>

              <button
                type="button"
                onClick={() => {
                  setFileToDelete(previewFile);
                  setPreviewFile(null);
                  setDeleteConfirmOpen(true);
                }}
                className="px-4 py-2 rounded-xl bg-rose-950/60 hover:bg-rose-600 text-rose-300 hover:text-white text-xs font-semibold border border-rose-800/60 transition"
              >
                Delete File
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setFileToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Storage Asset"
        message={`Are you sure you want to permanently delete "${fileToDelete?.name}" from bucket "${fileToDelete?.bucket}"? This action cannot be undone.`}
        confirmText="Delete Asset"
        type="danger"
      />
    </div>
  );
}
